import { GpsDevice } from '@/types/gps';
import { SimpleListItem } from '@/components/ui/simple-list-item';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Smartphone, 
  Cpu, 
  ImageOff,
  Wifi,
  WifiOff
} from 'lucide-react';

interface DeviceSimpleListProps {
  devices: GpsDevice[];
  onDeviceClick: (device: GpsDevice) => void;
}

export function DeviceSimpleList({ devices, onDeviceClick }: DeviceSimpleListProps) {
  // Helper: ícone por modelo
  const getDeviceIcon = (model: string | null | undefined) => {
    if (!model) return Smartphone;
    
    const icons: Record<string, any> = {
      'ST_901': Smartphone, 'ST_902': Smartphone, 'ST_906': Cpu,
      'ST_915': Smartphone, 'ST_940': Cpu, 'TK103': Cpu,
      'TK116': Smartphone, 'TK119': Cpu, 'TK303': Cpu,
      'TK905': Smartphone, 'ST_907': Cpu, 'FMB130': Smartphone,
      'FMB910': Smartphone, 'FMB920': Smartphone, 'CRX3_MINI': Cpu,
      'RST_MINI': Smartphone, 'VL03': Cpu, 'UNKNOWN': ImageOff,
    };
    return icons[model] || Smartphone;
  };

  // Helper: status de conexão
  const getConnectionStatus = (device: GpsDevice) => {
    if (device.status === 'offline') {
      return { label: 'Offline', color: 'text-red-500', icon: WifiOff };
    }

    if (device.last_signal_at) {
      const lastSignal = new Date(device.last_signal_at);
      const now = new Date();
      const diffMinutes = (now.getTime() - lastSignal.getTime()) / 1000 / 60;
      
      if (diffMinutes <= 5) {
        return { label: 'Online', color: 'text-green-500', icon: Wifi };
      }
    }

    return { label: 'Offline', color: 'text-red-500', icon: WifiOff };
  };

  if (devices.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <Smartphone className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Nenhum equipamento cadastrado</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Equipamentos</span>
          <Badge variant="secondary">{devices.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {devices.map((device) => {
            const Icon = getDeviceIcon(device.model);
            const connectionStatus = getConnectionStatus(device);
            const ConnectionIcon = connectionStatus.icon;
            
            return (
              <SimpleListItem
                key={device.id}
                icon={Icon}
                title={device.code || device.imei_or_uid || 'Sem identificação'}
                subtitle="Rastreador"
                onClick={() => onDeviceClick(device)}
                rightContent={
                  <div className="flex items-center gap-2">
                    {/* Status de conexão */}
                    <div className="flex items-center gap-1">
                      <ConnectionIcon className={`h-4 w-4 ${connectionStatus.color}`} />
                    </div>
                    
                    {/* Badge de instalação */}
                    {device.vehicle_id ? (
                      <Badge className="bg-blue-500 hover:bg-blue-600 text-white text-xs">
                        Instalado
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        Não instalado
                      </Badge>
                    )}
                  </div>
                }
              />
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
